import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Store, ArrowRight, Users } from "lucide-react";

export default function AdminOverviewPage() {
    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/restaurants");
                if (active) setRestaurants(data || []);
            } catch (error) {
                console.warn("[admin-overview] load failed", error?.message);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    if (loading) return <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8"><Skeleton className="h-64 w-full" /></div>;

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
            <h1 className="font-display text-3xl">Admin overview</h1>
            <p className="text-sm text-muted-foreground mb-6">
                Every restaurant carries its own menu & floor plan — open a restaurant to manage.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <StatCard label="Restaurants" value={restaurants.length} icon={Store} />
                <StatCard label="Avg rating" value={avg(restaurants.map(r => r.rating)).toFixed(2)} />
                <StatCard label="Total reviews" value={sum(restaurants.map(r => r.review_count)).toLocaleString()} icon={Users} />
            </div>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-xl">Restaurants</h2>
                        <Link to="/admin/restaurants" className="text-sm text-primary inline-flex items-center" data-testid="admin-go-restaurants">
                            Manage all <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Link>
                    </div>
                    {restaurants.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-8">No restaurants yet.</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {restaurants.map(r => (
                                <Card
                                    key={r.id}
                                    className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                                    onClick={() => navigate(`/admin/restaurants/${r.id}`)}
                                    data-testid="admin-restaurant-tile"
                                >
                                    <div className="aspect-[16/9]"><img src={r.image_url} alt={r.name} className="h-full w-full object-cover" /></div>
                                    <CardContent className="p-3">
                                        <div className="font-display text-lg">{r.name}</div>
                                        <div className="text-xs text-muted-foreground">{r.cuisine} · {r.price_range}</div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function StatCard({ label, value, icon: Icon }) {
    return (
        <Card>
            <CardContent className="p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
                        <div className="font-display text-4xl mt-1">{value}</div>
                    </div>
                    {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
                </div>
            </CardContent>
        </Card>
    );
}

function sum(arr) { return arr.reduce((s, n) => s + (n || 0), 0); }
function avg(arr) { return arr.length ? sum(arr) / arr.length : 0; }
